import React from "react";
import MainLayout from "../layout/MainLayout";
import { motion } from "framer-motion";

export const PrivacyPolicy = () => {
  return (
    <MainLayout>
      <section className="max-w-4xl mx-auto px-4 py-16">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-4xl font-bold mb-6 text-center just-me-again-down-here-regular"
        >
          Privacy Policy
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-gray-700 dark:text-gray-300 mb-8 text-center"
        >
          Last updated: {new Date().getFullYear()}
        </motion.p>

        {/* INTRO */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-gray-700 dark:text-gray-300 mb-8 leading-relaxed"
        >
          This Privacy Policy describes how Muhaad Tour & Travel (“we”, “our”,
          or “us”) collects, uses, stores, and protects the personal information
          you provide when you use our website, contact us, or book our services.
          We are committed to ensuring that your privacy is protected and your
          data is handled responsibly.
        </motion.p>

        {/* SECTION 1 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-10"
        >
          <h2 className="text-2xl font-semibold mb-3">1. Information We Collect</h2>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            We may collect the following types of information:
          </p>
          <ul className="list-disc ml-6 mt-3 space-y-2 text-gray-700 dark:text-gray-300">
            <li>Full name, email address, and phone number</li>
            <li>Booking details and travel preferences</li>
            <li>Location details for pick-up and drop-off services</li>
            <li>Payment information (processed securely through third-party providers)</li>
            <li>Website usage data and device information</li>
            <li>Any information you voluntarily submit through forms or inquiries</li>
          </ul>
        </motion.div>

        {/* SECTION 2 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mb-10"
        >
          <h2 className="text-2xl font-semibold mb-3">
            2. How We Use Your Information
          </h2>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            We use your information to:
          </p>
          <ul className="list-disc ml-6 mt-3 space-y-2 text-gray-700 dark:text-gray-300">
            <li>Process bookings and provide car hire or travel services</li>
            <li>Improve customer service and communication</li>
            <li>Send confirmations, updates, and support messages</li>
            <li>Enhance our website’s performance and user experience</li>
            <li>Comply with legal and regulatory requirements</li>
          </ul>
        </motion.div>

        {/* SECTION 3 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mb-10"
        >
          <h2 className="text-2xl font-semibold mb-3">3. Sharing Your Information</h2>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            We do not sell your personal information. However, we may share it with:
          </p>

          <ul className="list-disc ml-6 mt-3 space-y-2 text-gray-700 dark:text-gray-300">
            <li>Trusted drivers and partners necessary to complete bookings</li>
            <li>Payment processors to securely handle transactions</li>
            <li>Government authorities if required by law</li>
            <li>Service providers who help us operate our business (e.g., hosting, analytics)</li>
          </ul>
        </motion.div>

        {/* SECTION 4 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="mb-10"
        >
          <h2 className="text-2xl font-semibold mb-3">4. Data Security</h2>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            We take data security seriously. We use encryption, secure servers,
            and access controls to protect your information from unauthorized
            access, loss, or misuse. However, no method of internet transmission
            is 100% secure.
          </p>
        </motion.div>

        {/* SECTION 5 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="mb-10"
        >
          <h2 className="text-2xl font-semibold mb-3">5. Your Rights</h2>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            Depending on your location, you may have rights such as:
          </p>
          <ul className="list-disc ml-6 mt-3 space-y-2 text-gray-700 dark:text-gray-300">
            <li>Requesting a copy of the information we hold about you</li>
            <li>Requesting corrections to inaccurate data</li>
            <li>Requesting deletion of your information</li>
            <li>Opting out of marketing messages</li>
          </ul>
        </motion.div>

        {/* SECTION 6 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="mb-10"
        >
          <h2 className="text-2xl font-semibold mb-3">6. Cookies & Tracking</h2>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            Our website may use cookies and analytics tools to improve
            performance, personalize your experience, and understand user
            behavior. You can disable cookies in your browser settings.
          </p>
        </motion.div>

        {/* SECTION 7 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className="mb-10"
        >
          <h2 className="text-2xl font-semibold mb-3">
            7. Changes to This Privacy Policy
          </h2>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            We may update this Privacy Policy from time to time. Any changes will
            be posted on this page with a new “Last Updated” date.
          </p>
        </motion.div>

        {/* CONTACT */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1 }}
        >
          <h2 className="text-2xl font-semibold mb-3">8. Contact Us</h2>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            If you have any questions about this Privacy Policy or how we handle
            your information, you may contact us at:
          </p>

          <div className="mt-4 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
            <p className="text-gray-700 dark:text-gray-300">
              <strong>Muhaad Tour & Travel</strong><br />
              Kampala, Uganda<br />
              Phone: +256 773 222402<br />
              Email: muhaadsolutions@gmail.com
            </p>
          </div>
        </motion.div>
      </section>
    </MainLayout>
  );
};

